// src/components/locutor/DestaquesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';

const API = '';

const ArrowIcon = ({ direction = 'right', size = 12, color = '#94a3b8' }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d={direction === 'right' ? 'M2 6h8M7 3l3 3-3 3' : 'M10 6H2M5 3L2 6l3 3'}
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function gerarId() {
  return Math.random().toString(36).slice(2, 10);
}

function normalizarTextoSecao(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizarReferenciaSecao(value) {
  if (value?.general === true || normalizarTextoSecao(value?.sectionName || value?.secao || value) === 'geral') {
    return { general: true, sectionName: 'Geral', sectionKey: null, sectionId: null };
  }
  const source = typeof value === 'string' ? { sectionName: value } : (value || {});
  const sectionName = String(source.sectionName || source.nome || source.secao || '').trim();
  const sectionId = String(source.sectionId || source.secaoId || source.id || '').trim();
  const sectionKey = String(source.sectionKey || source.secaoKey || '').trim()
    || (sectionId
      ? `section-id:${sectionId}`
      : (sectionName ? `section-name:${normalizarTextoSecao(sectionName)}` : ''));
  if (!sectionName || !sectionKey) return null;
  return { general: false, sectionName, sectionKey, sectionId: sectionId || null };
}

function precoCanonicoValido(value) {
  return !!value
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).sort().join(',') === 'centavos,moeda'
    && value.moeda === 'BRL'
    && Number.isSafeInteger(value.centavos)
    && value.centavos >= 0;
}

function formatarPrecoCanonico(value) {
  if (!precoCanonicoValido(value)) return '';
  const reais = Math.floor(value.centavos / 100);
  const centavos = String(value.centavos % 100).padStart(2, '0');
  const agrupado = String(reais).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${agrupado},${centavos}`;
}

function formatarPrecoItem(item) {
  const canonico = formatarPrecoCanonico(item?.precoCanonico);
  if (canonico) return canonico;
  return typeof item?.preco === 'string' ? item.preco.trim() : '';
}

function destaqueGeralLegado(item) {
  return normalizarTextoSecao(item?.sectionName || item?.secao) === 'geral'
    && !item?.sectionId
    && !item?.sectionKey;
}

function interpretarPrecoBrl(value) {
  const original = String(value || '').trim();
  if (!original) return { ok: true, precoCanonico: null, preco: '' };

  const normalizado = original.replace(/^R\$\s*/iu, '').replace(/\s+/gu, '');
  if (!/^(?:\d{1,3}(?:\.\d{3})+|\d+),\d{2}$/u.test(normalizado)) {
    return { ok: false, precoCanonico: null, preco: '' };
  }

  const [reaisTexto, centavosTexto] = normalizado.split(',');
  const reais = Number(reaisTexto.replace(/\./gu, ''));
  const centavos = Number(centavosTexto);
  const totalCentavos = (reais * 100) + centavos;
  if (!Number.isSafeInteger(totalCentavos) || totalCentavos < 0) {
    return { ok: false, precoCanonico: null, preco: '' };
  }

  const precoCanonico = { moeda: 'BRL', centavos: totalCentavos };
  return {
    ok: true,
    precoCanonico,
    preco: formatarPrecoCanonico(precoCanonico),
  };
}

export function DestaquesPanel({ clienteId, secoesDisponiveis = [] }) {
  const [itens,      setItens]      = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando,   setSalvando]   = useState(false);
  const [novo,       setNovo]       = useState({ produto: '', preco: '', integral: false });
  const [expandido,  setExpandido]  = useState(false);
  const [passo,      setPasso]      = useState(1);
  const [rascunho,   setRascunho]   = useState(null);
  const [erro,        setErro]       = useState('');

  const referenciasSecao = React.useMemo(() => {
    const refs = secoesDisponiveis
      .map(normalizarReferenciaSecao)
      .filter(ref => ref && !ref.general);
    return refs;
  }, [secoesDisponiveis]);

  const nomesDuplicados = React.useMemo(() => {
    const counts = new Map();
    referenciasSecao.filter(ref => !ref.general).forEach(ref => {
      const key = normalizarTextoSecao(ref.sectionName);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key));
  }, [referenciasSecao]);

  const carregar = useCallback(async () => {
    if (!clienteId) return;
    setCarregando(true);
    try {
      const r = await fetch(`${API}/api/destaques/${clienteId}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.erro || 'Erro ao carregar destaques');
      setItens(d.itens || []);
      setErro('');
    } catch (error) {
      setErro(error.message || 'Erro ao carregar destaques');
    } finally { setCarregando(false); }
  }, [clienteId]);

  useEffect(() => { carregar(); }, [carregar]);

  async function salvar(lista) {
    setSalvando(true);
    try {
      const response = await fetch(`${API}/api/destaques/${clienteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itens: lista }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.erro || 'Erro ao salvar destaques');
      setErro('');
      return payload.itens || lista;
    } finally { setSalvando(false); }
  }

  function handleAdicionar() {
    if (!novo.produto.trim()) return;
    const precoInterpretado = interpretarPrecoBrl(novo.preco);
    if (!precoInterpretado.ok) {
      setErro('Preço inválido. Use reais e centavos, por exemplo: R$ 8,99.');
      return;
    }
    setErro('');
    setRascunho({
      produto: novo.produto.trim(),
      preco: precoInterpretado.preco,
      precoCanonico: precoInterpretado.precoCanonico,
      integral: novo.integral,
    });
    setPasso(2);
  }

  async function handleSalvarComSecao(secaoRef) {
    const item = {
      id: gerarId(),
      produto:  rascunho.produto,
      secao: secaoRef.sectionName,
      sectionName: secaoRef.sectionName,
      sectionKey: secaoRef.sectionKey,
      sectionId: secaoRef.sectionId,
      secaoId: secaoRef.sectionId,
      preco:    rascunho.preco,
      ...(rascunho.precoCanonico ? { precoCanonico: rascunho.precoCanonico } : {}),
      integral: rascunho.integral,
      ativo:    true,
      criadoEm: new Date().toISOString(),
    };
    const nova = [...itens, item];
    try {
      setItens(await salvar(nova));
      setPasso(1);
      setRascunho(null);
      setNovo({ produto: '', preco: '', integral: false });
      setExpandido(false);
    } catch (error) {
      setErro(error.message || 'Erro ao salvar destaque');
    }
  }

  async function toggleAtivo(id) {
    const nova = itens.map(i => i.id === id ? { ...i, ativo: !i.ativo } : i);
    try {
      setItens(await salvar(nova));
    } catch (error) {
      setErro(error.message || 'Erro ao atualizar destaque');
    }
  }

  async function remover(id) {
    const nova = itens.filter(i => i.id !== id);
    try {
      setItens(await salvar(nova));
    } catch (error) {
      setErro(error.message || 'Erro ao remover destaque');
    }
  }

  const ativos  = itens.filter(i => i.ativo).length;

  return (
    <div style={{ padding: '0 0 4px' }}>

      {/* Header com contagem */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
          O dispatcher consulta os destaques antes da rota e respeita rotação, cooldown e gates do degrau. Eles desativam ao fechar a loja.
          {ativos > 0 && <span style={{ marginLeft: 6, color: 'var(--brand-400)', fontWeight: 500 }}>{ativos} ativo{ativos > 1 ? 's' : ''}</span>}
        </p>
      </div>

      {carregando && (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>Carregando...</p>
      )}

      {erro && (
        <p role="alert" style={{ fontSize: 12, color: 'var(--danger)', padding: '6px 0', margin: 0 }}>
          {erro}
        </p>
      )}

      {!carregando && itens.length === 0 && !expandido && (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', padding: '4px 0 10px' }}>
          Nenhum destaque hoje.
        </p>
      )}

      {/* Lista de itens */}
      {itens.map(item => (
        <div key={item.id} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 12px', borderRadius: 0, marginBottom: 1,
          background: item.ativo ? 'rgba(var(--brand-rgb), 0.1)' : 'var(--border)',
          border: `0.5px solid ${item.ativo ? 'rgba(var(--brand-rgb), 0.3)' : 'var(--border)'}`,
          opacity: item.ativo ? 1 : 0.55,
          transition: 'opacity 0.2s',
        }}>
          {/* Toggle */}
          <button disabled={salvando || destaqueGeralLegado(item)} onClick={() => toggleAtivo(item.id)} title={destaqueGeralLegado(item) ? 'Geral legado não possui escopo factual de loja' : (item.ativo ? 'Pausar' : 'Ativar')} style={{
            width: 34, height: 20, borderRadius: 10, border: 'none',
            cursor: 'pointer', flexShrink: 0, position: 'relative',
            background: item.ativo ? 'var(--brand-400)' : 'var(--border)',
            transition: 'background 0.2s',
          }}>
            <span style={{
              position: 'absolute', top: 3, left: item.ativo ? 16 : 2,
              width: 14, height: 14, borderRadius: '50%',
              background: 'var(--bg-surface)',
              transition: 'left 0.2s',
            }} />
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.produto}
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 5, background: 'var(--border)', color: 'var(--text-secondary)', border: '0.5px solid var(--border)' }}>
                {item.sectionName || item.secao}
                {item.sectionId ? ` · ${item.sectionId}` : ''}
              </span>
              {formatarPrecoItem(item) && (
                <span
                  title={precoCanonicoValido(item.precoCanonico) ? 'Preço canônico apto para a composição factual' : 'Preço legado: visível, mas não falado'}
                  style={{ fontSize: 11, padding: '1px 6px', borderRadius: 5, background: 'rgba(var(--warning-rgb), 0.1)', color: 'var(--warning)', border: '0.5px solid rgba(var(--warning-rgb), 0.3)' }}
                >
                  {formatarPrecoItem(item)}
                </span>
              )}
              {formatarPrecoItem(item) && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, background: 'var(--border)', color: 'var(--text-muted)', border: '0.5px solid var(--border)' }}>
                  {precoCanonicoValido(item.precoCanonico) ? 'preço apto para fala' : 'preço legado · não falado'}
                </span>
              )}
              {item.integral && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, background: 'var(--border)', color: 'var(--text-muted)', border: '0.5px solid var(--border)' }}>
                  integral
                </span>
              )}
              {destaqueGeralLegado(item) && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, background: 'rgba(var(--warning-rgb), 0.1)', color: 'var(--warning)', border: '0.5px solid rgba(var(--warning-rgb), 0.3)' }}>
                  Geral legado · inativo e não falado
                </span>
              )}
            </div>
          </div>

          <button disabled={salvando} onClick={() => remover(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>
            ×
          </button>
        </div>
      ))}

      {/* Botão de abrir formulário */}
      {!expandido && (
        <button
          onClick={() => setExpandido(true)}
          style={{
            width: '100%', padding: '9px', borderRadius: 8, marginTop: 4,
            border: '0.5px dashed var(--border)',
            background: 'transparent', color: 'var(--text-secondary)',
            fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 6,
          }}
        >
          + Adicionar destaque
        </button>
      )}

      {/* Passo 1 — formulário */}
      {expandido && passo === 1 && (
        <div style={{ marginTop: 8, padding: '14px 14px 12px', background: 'var(--border)', border: '0.5px solid var(--border)', borderRadius: 0 }}>
          <textarea
            autoFocus
            placeholder="Informe um produto ou destaque factual&#10;Ex: Maionese Hellmann's 500g&#10;Ex: Picanha bovina"
            value={novo.produto}
            onChange={e => setNovo(p => ({ ...p, produto: e.target.value }))}
            rows={2}
            maxLength={120}
            style={{ width: '100%', fontSize: 14, borderRadius: 8, resize: 'vertical', lineHeight: 1.5, marginBottom: 10, border: '0.5px solid var(--border)' }}
          />

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Preço (opcional)</label>
            <input
              placeholder="R$ 8,99"
              value={novo.preco}
              onChange={e => setNovo(p => ({ ...p, preco: e.target.value }))}
              inputMode="decimal"
              maxLength={20}
              style={{ width: 140, fontSize: 13 }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Use reais e dois centavos, por exemplo R$ 8,99. O novo cadastro gera preço canônico apto para a composição factual; preços legados não são convertidos automaticamente.
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={novo.integral} onChange={e => setNovo(p => ({ ...p, integral: e.target.checked }))} />
              Modo integral
              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>— texto exato somente com seção, gates e política regulatória válidos</span>
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => { setExpandido(false); setPasso(1); setRascunho(null); setNovo({ produto: '', preco: '', integral: false }); }} style={{ padding: '7px 12px', fontSize: 13 }}>
                Cancelar
              </button>
              <button onClick={handleAdicionar} disabled={!novo.produto.trim()} style={{ padding: '7px 14px', fontSize: 13, fontWeight: 500 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  Próximo <ArrowIcon direction="right" size={11} />
                </span>
              </button>
            </div>
          </div>

          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '8px 0 0' }}>
            O cadastro não infere qualidade, frescor, promoção nem estoque. No M1, o modo não integral declara apenas a existência do produto.
            Destaques válidos entram em uma fila persistida com rotação e cooldown antes da rota normal.
          </p>
        </div>
      )}

      {/* Passo 2 — seleção de setor */}
      {expandido && passo === 2 && (
        <div style={{ marginTop: 8, padding: '14px 14px 12px', background: 'var(--border)', border: '0.5px solid var(--border)', borderRadius: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <button onClick={() => setPasso(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, padding: '2px 6px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <ArrowIcon direction="left" size={11} /> Voltar
              </span>
            </button>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Em qual setor colocar?</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
            {referenciasSecao.map(secaoRef => (
              <button
                key={secaoRef.sectionKey}
                onClick={() => handleSalvarComSecao(secaoRef)}
                disabled={salvando}
                style={{ padding: '10px 8px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--border)', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer' }}
              >
                {secaoRef.sectionName}
                {nomesDuplicados.has(normalizarTextoSecao(secaoRef.sectionName))
                  ? ` · ${secaoRef.sectionId || secaoRef.sectionKey}`
                  : ''}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
